import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LiveLedgerViewer — real-time tail of the immutable audit ledger.
 *
 * Polls /api/audit/ledger every 6 seconds and displays the most recent
 * events with their prev_hash → event_hash chain. Server-side
 * verification is surfaced as a green "CHAIN VERIFIED" badge; if any
 * link in the chain is broken the badge flips red so booth visitors
 * see tamper-evidence live.
 */

const EVENT_STYLE = {
  photo:      { color: "#5AC8D8", bg: "rgba(90,200,216,0.10)" },
  sign_off:   { color: "#00DC78", bg: "rgba(0,220,120,0.10)" },
  approval:   { color: "#00DC78", bg: "rgba(0,220,120,0.10)" },
  deficiency: { color: "#FFB347", bg: "rgba(255,179,71,0.10)" },
  fail:       { color: "#F87171", bg: "rgba(248,113,113,0.10)" },
};

const truncHash = (h) => {
  if (!h) return "—";
  if (h === "GENESIS") return "GENESIS";
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
};

const LiveLedgerViewer = () => {
  const [entries, setEntries] = useState([]);
  const [verified, setVerified] = useState(true);
  const [error, setError] = useState("");
  const [lastPoll, setLastPoll] = useState("");

  const poll = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/audit/ledger?limit=25`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setEntries((d.entries || []).slice().reverse());
        setVerified(!!d.verified);
        setError("");
        setLastPoll(new Date().toLocaleTimeString());
      } else if (r.status === 401) {
        setError("Sign in to view the immutable audit ledger.");
      }
    } catch {
      setError("Ledger stream temporarily unreachable.");
    }
  };

  useEffect(() => {
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div data-testid="live-ledger-viewer" style={{
      background: "rgba(0,255,255,0.03)",
      border: `1px solid ${verified ? "rgba(0,255,255,0.28)" : "rgba(248,113,113,0.48)"}`,
      borderRadius: 12,
      padding: "14px 16px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800 }}>
            &gt; IMMUTABLE AUDIT LEDGER · LIVE TAIL
          </div>
          <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800, marginTop: 3 }}>
            Zero-trust hash chain · 150m geofence + HMAC handshake enforced
          </div>
        </div>
        <div style={{
          padding: "3px 8px", borderRadius: 3,
          background: verified ? "rgba(0,220,120,0.15)" : "rgba(248,113,113,0.18)",
          color: verified ? "#00DC78" : "#F87171",
          fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem",
          letterSpacing: "0.16em", fontWeight: 800,
        }} data-testid="ledger-verified-badge">
          {verified ? "✓ CHAIN VERIFIED" : "✗ CHAIN BROKEN"}
        </div>
      </div>

      {lastPoll && (
        <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", marginBottom: 8 }}>
          LAST POLL · {lastPoll} · refreshing every 6s
        </div>
      )}

      {error && (
        <div style={{ color: "#F87171", fontSize: "0.82rem", padding: "0.5rem 0" }}>
          {error}
        </div>
      )}

      {!error && entries.length === 0 && (
        <div style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.5rem 0" }}>
          Ledger is empty. Field crew photo uploads through the geofenced Field Mode session will materialize here.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto" }}>
        {entries.map(e => {
          const s = EVENT_STYLE[e.event_type] || { color: "#B4BEC8", bg: "rgba(255,255,255,0.03)" };
          return (
            <div key={e.event_id || e.event_hash} data-testid={`ledger-entry-${e.event_id}`}
                 style={{
                   background: s.bg, border: `1px solid ${s.color}30`,
                   borderRadius: 6, padding: "8px 10px",
                 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                <div style={{ color: s.color, fontWeight: 800, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {e.event_type}
                </div>
                <div style={{ color: "#7E92A8", fontSize: "0.68rem" }}>
                  {new Date(e.recorded_at).toLocaleString()}
                </div>
              </div>
              <div style={{ color: "#B4BEC8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", marginTop: 4 }}>
                session <strong style={{ color: "#fff" }}>{e.session_id}</strong> · by {e.recorded_by} · {typeof e.distance_m === "number" ? `${e.distance_m}m from site` : ""}
              </div>
              <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", marginTop: 3, wordBreak: "break-all" }}>
                {truncHash(e.prev_hash)} → <span style={{ color: s.color }}>{truncHash(e.event_hash)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveLedgerViewer;
